import { Box, FormControl, Grid, Typography } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { OLButton } from "OLButton";
import { hasAnyAccessToResource } from "api/general";
import { useAppSelector } from "app/hooks";
import { AdditionalAttribute } from "components/Campaign/CampaignConditions/AdditionalAttribute";
import { SelectInput } from "components/FormControls/Select/Select";
import { AttributeSection } from "components/Layout/Campaign/Common/CampaignRules/Rule/Conditions/AttributeSection";
import { Tooltip } from "components/Tooltips/Tooltip";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultWalletData } from "reduxState/defaultWalletData/defaultWalletDataSlice";
import {
  CampaignCondition,
  CampaignConditionNewFormat,
  CampaignConditionType,
  CampaignRuleTarget,
  CampaignTypes,
  ItemCriteriaParam,
  SelectedSchemaCondition,
  TransactionItem,
} from "types/campaigns";
import { alphabetizeByProperty } from "utilities/arrays";
import { markEditResolved } from "utilities/checkRulesEditMode";
import { findConditionIdPath, isNewFormat } from "../../../campaignHelpers";
import { ConditionReadOnlyWrapper } from "./ConditionReadOnlyWrapper";
import { NewFormatCondition } from "./NewFormatConditions/NewFormatCondition";
import { checkIsAttributeSuppressedHj } from "./utils";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    conditionWrapper: {
      background: theme.gray50,
      border: `1px solid ${theme.gray200}`,
      borderLeft: "none",
      borderRight: "none",
      display: "flex",
      flexDirection: "column",
      padding: theme.spacing(6, 8),
      position: "relative",
      width: "100%",
      "& + &": {
        marginTop: theme.spacing(8),
      },
    },
    conditionHeader: {
      fontSize: 14,
      fontWeight: 500,
      marginBottom: theme.spacing(6),
    },
    saveButton: {
      padding: theme.spacing(4, 11),
    },
    clearIcon: {
      position: "absolute",
      top: "8px",
      right: "8px",
    },
    input: {
      backgroundColor: theme.white50,
    },
    formControl: {
      width: "100%",
    },
    buttonWrapper: {
      marginTop: theme.spacing(12),
      display: "flex",
      justifyContent: "flex-end",
      gap: theme.spacing(8),
    },
    achievementConditionWrapper: {
      "&:last-child": {
        borderBottom: "none",
        borderRadius: "0 0 8px 8px",
      },
    },
    andOperator: {
      color: theme.gray500,
      margin: theme.spacing(2, 0, 2, 8),
    },
    additionalMargin: {
      margin: theme.spacing(6, 0),
      "&:last-child": {
        marginBottom: 0,
      },
    },
  }),
);

export type ConditionError = {
  data?: string[] | { from: string[]; to: string[] };
  operator?: string[];
  param?: string;
};

interface Props {
  rule: any;
  input: { attribute: string; type?: CampaignConditionType };
  conditions: (
    | CampaignCondition
    | SelectedSchemaCondition
    | CampaignConditionNewFormat
  )[];
  index: number;
  ruleIndex: number;
  remove: (condtionIndex: number) => void;
  replace: (conditionIndex: number, value: any) => void;
  condition: any;
  setFieldValue: (field: string, value: any) => void;
  insert: (conditionIndex: number, value: any) => void;
  setFieldError: (field: string, errorMsg?: any) => void;
  error?: ConditionError;
  isAchievementForm?: boolean;
  target?: CampaignRuleTarget;
  transactionItemsFilters?: TransactionItem[];
}

export const Condition = ({
  rule,
  input,
  conditions,
  index,
  ruleIndex,
  remove,
  replace,
  condition,
  setFieldValue,
  insert,
  setFieldError,
  error,
  isAchievementForm,
  target,
  transactionItemsFilters,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const hasAnyTierPermission = hasAnyAccessToResource("LEVEL");
  const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");
  const { currency } = useAppSelector(selectCurrentStoreData);
  const defaultWallet = useAppSelector(selectDefaultWalletData);
  const { isEditing, isSaved } = condition;
  const fieldKey = isEditing
    ? `rules.[${ruleIndex}].conditions.[${index}].draft`
    : `rules.[${ruleIndex}].conditions.[${index}]`;
  const ruleData = isEditing
    ? rule.conditions?.[index]?.draft
    : rule.conditions?.[index];
  // Referral conditions have target key; rule.type for achievement conditions
  const isRefferalCampaign =
    rule.target || rule.type === CampaignTypes.REFERRAL;
  const isAndOperatorVisible = index + 1 < rule.conditions.length;
  const isSaveConditionButtonDisabled =
    ruleData.type === "itemsCriteria" &&
    !ruleData?.param?.find((item: ItemCriteriaParam) => item.values.length > 0);
  const shouldSuppressHj = checkIsAttributeSuppressedHj(ruleData?.attribute);
  const attributeFieldName = isNewFormat(ruleData)
    ? `${fieldKey}.value.field`
    : `${fieldKey}.attribute`;

  return (
    <>
      {isEditing ? (
        <>
          <section
            className={`${classes.conditionWrapper} ${
              classes.additionalMargin
            } ${isAchievementForm ? classes.achievementConditionWrapper : ""}`}
            data-test-id={`condition-${index}-container`}
            data-attr-edit={`condition-${ruleIndex}-${index}`}
          >
            <Typography className={classes.conditionHeader}>
              {isSaved
                ? message({ id: "editCondition" })
                : message({ id: "newCondition" })}
            </Typography>
            <Grid container spacing={12}>
              <Grid item xs={6}>
                <FormControl className={classes.formControl}>
                  <SelectInput
                    label={message({
                      id: "conditionType",
                    })}
                    displaySearch
                    name={attributeFieldName}
                    items={
                      alphabetizeByProperty(
                        conditions.map((condition) => ({
                          labelInfo:
                            "labelInfo" in condition && condition.labelInfo,
                          label: message(
                            { id: condition.label },
                            {
                              walletName: defaultWallet?.name,
                              units: defaultWallet?.unitPluralName,
                              currency,
                            },
                          ),
                          id: findConditionIdPath(condition),
                          disabled:
                            (!hasAnyTierPermission &&
                              findConditionIdPath(condition) ===
                                "customer.levelId") ||
                            (!hasAnyTierPermission &&
                              findConditionIdPath(condition) ===
                                "customer.tiers") ||
                            (!hasAnyChannelPermission &&
                              findConditionIdPath(condition) ===
                                "transaction.channelId"),
                          isDeprecated: (condition as CampaignCondition)
                            .isDeprecated,
                        })),
                        "label",
                      ) || []
                    }
                    data-test-id="condition-type"
                    isCustomSelect
                    onChange={(e, value) => {
                      const selected = conditions.find(
                        (condition) => findConditionIdPath(condition) === value,
                      );

                      if (selected) {
                        const newValue = {
                          draft: selected,
                          isEditing: true,
                          isSaved: false,
                        };

                        replace(index, newValue);
                      }
                    }}
                  />
                </FormControl>
              </Grid>
              {isNewFormat(ruleData) ? (
                <NewFormatCondition
                  fieldKey={fieldKey}
                  transactionItemsFilters={transactionItemsFilters}
                  ruleData={ruleData}
                />
              ) : (
                <>
                  <AdditionalAttribute
                    attribute={ruleData.attribute || ""}
                    fieldName={fieldKey}
                  />
                  <AttributeSection
                    disabled={
                      !hasAnyTierPermission &&
                      input.attribute === "customer.levelId"
                    }
                    fieldName={fieldKey}
                    type={input.type}
                    index={index}
                    ruleIndex={ruleIndex}
                    isAchievementForm={isAchievementForm}
                    shouldSuppressHj={shouldSuppressHj}
                  />
                </>
              )}
            </Grid>
            <div className={classes.buttonWrapper}>
              <OLButton
                data-test-id="condition-cancel"
                onClick={() => {
                  markEditResolved(`condition-${ruleIndex}-${index}`);
                  if (isSaved) {
                    setFieldValue(
                      `rules.[${ruleIndex}].conditions.[${index}]`,
                      {
                        ...condition,
                        isEditing: false,
                      },
                    );
                  } else {
                    remove(index);
                  }
                }}
                size="small"
              >
                {isSaved ? message({ id: "close" }) : message({ id: "cancel" })}
              </OLButton>
              <Tooltip
                text={
                  isSaveConditionButtonDisabled
                    ? message({ id: "addAtLeastOneCriterion" })
                    : ""
                }
              >
                <div>
                  <OLButton
                    data-test-id="condition-save"
                    disabled={isSaveConditionButtonDisabled}
                    onClick={() => {
                      markEditResolved(`condition-${ruleIndex}-${index}`);
                      // Save value and remove draft key
                      setFieldValue(
                        `rules.[${ruleIndex}].conditions.[${index}]`,
                        {
                          ...condition.draft,
                          isEditing: false,
                          isSaved: true,
                        },
                      );

                      setFieldError(
                        `rules.[${ruleIndex}].conditions.[${index}]`,
                        undefined,
                      );
                    }}
                    variant="contained"
                    size="small"
                  >
                    {message({ id: "saveCondition" })}
                  </OLButton>
                </div>
              </Tooltip>
            </div>
          </section>
          {isAndOperatorVisible && (
            <Box className={classes.andOperator}>
              {message({
                id: "and",
              }).toUpperCase()}
            </Box>
          )}
        </>
      ) : (
        <>
          <ConditionReadOnlyWrapper
            data-test-id="condition-saved"
            condition={{ ...condition, target: target }}
            isRefferalCampaign={isRefferalCampaign}
            remove={() => remove(index)}
            edit={() => {
              // Move errors to draft view
              setFieldError(
                `rules.[${ruleIndex}].conditions.[${index}].draft`,
                error,
              );

              setFieldValue(fieldKey, {
                ...condition,
                draft: { ...condition },
                isEditing: true,
                isSaved: true,
              });
            }}
            duplicate={() =>
              insert(index + 1, {
                draft: { ...condition },
                isEditing: true,
                isSaved: false,
              })
            }
            index={index}
            error={error}
            isAchievementForm={isAchievementForm}
            transactionItemsFilters={transactionItemsFilters}
          />
          {isAndOperatorVisible && (
            <Box className={classes.andOperator}>
              {message({
                id: "and",
              }).toUpperCase()}
            </Box>
          )}
        </>
      )}
    </>
  );
};
